import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconBuilding, IconCheck, IconEdit, IconPlus, IconTrash } from '../components/icons';
import { Badge, Card, EmptyState, ErrorMessage, Field, Loading, Modal, ModuleHeader, SearchSelect, useAsync, useToast } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/format';

export function PropertiesPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const { notify } = useToast();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);

  const properties = useAsync(
    () => api.get('/api/properties', { search, includeInactive: showInactive ? 'true' : '' }),
    [search, showInactive],
  );
  const customers = useAsync(() => api.get('/api/customers').catch(() => []), []);

  /**
   * Stilllegen oder endgültig löschen.
   *
   * Der Regelfall ist das Stilllegen: Ein Objekt hängt an Aufgaben, Zeiten
   * und Buchungen. Endgültig entfernen lässt sich nur, was noch nirgends
   * verwendet wird – der Server prüft das und meldet es im Klartext.
   */
  const remove = async (property: any, permanent: boolean) => {
    const question = permanent
      ? `Objekt „${property.name}“ endgültig löschen? Das ist nur möglich, solange keine Aufträge daran hängen.`
      : `Objekt „${property.name}“ stilllegen? Es bleibt mit seiner Historie erhalten.`;
    if (!window.confirm(question)) return;

    try {
      await api.delete(`/api/properties/${property.id}`, permanent ? { permanent: 'true' } : undefined);
      notify(permanent ? 'Objekt gelöscht.' : 'Objekt stillgelegt.', 'success');
      properties.reload();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Aktion nicht möglich.', 'error');
    }
  };

  const reactivate = async (property: any) => {
    await api.post(`/api/properties/${property.id}/reactivate`);
    notify('Objekt wieder aktiv.', 'success');
    properties.reload();
  };

  const mayManage = can('properties.update') || can('properties.delete');

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconBuilding width={22} height={22} />}
        title="Objektdaten & Liegenschaften"
        subtitle={
          <>
            Objektstamm mit Ansprechpartnern, Zugangsinformationen und QR-Code für die niederschwellige Meldung
            durch Mieter. Zugangsdaten sind nur mit dem Recht <span className="mono">properties.access</span> sichtbar.
          </>
        }
        pill="Stammdaten"
        tone="accent"
        actions={
          can('properties.create') ? (
            <button className="btn primary" onClick={() => setEditing({ isNew: true })}>
              <IconPlus width={15} height={15} /> Objekt anlegen
            </button>
          ) : undefined
        }
      />

      <Card noBody>
        <div className="card-body tight">
          <div className="row">
            <input
              type="search"
              placeholder="Objekt, Ort oder Referenznummer suchen …"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              style={{ maxWidth: 380 }}
            />
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(event) => setShowInactive(event.target.checked)}
              />
              Stillgelegte anzeigen
            </label>
          </div>
        </div>

        {properties.loading ? (
          <Loading />
        ) : !properties.data?.length ? (
          <EmptyState icon={<IconBuilding width={20} height={20} />} title="Keine Objekte gefunden" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Objekt</th>
                  <th>Eigentümer/Verwalter</th>
                  <th>Adresse</th>
                  <th>Art</th>
                  <th className="num">Einheiten</th>
                  <th className="num">Offene Aufträge</th>
                  {mayManage && <th className="actions">Aktionen</th>}
                </tr>
              </thead>
              <tbody>
                {properties.data.map((property: any) => (
                  <tr
                    key={property.id}
                    className="clickable"
                    style={property.is_active ? undefined : { opacity: 0.55 }}
                    onClick={() => navigate(`/properties/${property.id}`)}
                  >
                    <td>
                      <strong>{property.name}</strong>
                      {!property.is_active && (
                        <span style={{ marginLeft: 8 }}>
                          <Badge>Stillgelegt</Badge>
                        </span>
                      )}
                      <div className="small subtle mono">{property.reference_no}</div>
                    </td>
                    <td className="small">{property.customer_name ?? '–'}</td>
                    <td className="small">
                      {property.street}
                      <div className="subtle">
                        {property.zip} {property.city}
                      </div>
                    </td>
                    <td className="small">{property.property_type ?? '–'}</td>
                    <td className="num">{property.units_count ?? '–'}</td>
                    <td className="num">
                      <Badge variant={property.open_tasks > 0 ? 'warning' : ''}>{property.open_tasks}</Badge>
                    </td>
                    {mayManage && (
                      <td className="actions" onClick={(event) => event.stopPropagation()}>
                        {can('properties.update') && (
                          <button className="btn sm" onClick={() => setEditing(property)}>
                            <IconEdit width={13} height={13} /> Bearbeiten
                          </button>
                        )}
                        {can('properties.delete') &&
                          (property.is_active ? (
                            <>
                              <button
                                className="btn sm ghost"
                                title="Stilllegen – bleibt mit Historie erhalten"
                                style={{ marginLeft: 6 }}
                                onClick={() => remove(property, false)}
                              >
                                Stilllegen
                              </button>
                              <button
                                className="btn sm ghost"
                                title="Endgültig löschen – nur ohne Verknüpfungen möglich"
                                onClick={() => remove(property, true)}
                              >
                                <IconTrash width={13} height={13} />
                              </button>
                            </>
                          ) : (
                            <button className="btn sm ghost" style={{ marginLeft: 6 }} onClick={() => reactivate(property)}>
                              <IconCheck width={13} height={13} /> Aktivieren
                            </button>
                          ))}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <PropertyModal
          property={editing}
          customers={customers.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            properties.reload();
            notify(editing.isNew ? 'Objekt angelegt.' : 'Objekt gespeichert.', 'success');
          }}
        />
      )}
    </div>
  );
}

function PropertyModal({
  property,
  customers,
  onClose,
  onSaved,
}: {
  property: any;
  customers: any[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = Boolean(property.isNew);
  const [form, setForm] = useState({
    name: '',
    customerId: '',
    referenceNo: '',
    street: '',
    zip: '',
    city: '',
    propertyType: '',
    unitsCount: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    latitude: '',
    longitude: '',
    accessNotes: '',
    geofenceRadius: '',
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  // Beim Bearbeiten den vollständigen Datensatz nachladen: Die Liste liefert
  // Zugangsdaten und Notizen bewusst nicht mit.
  const detail = useAsync(
    () => (isNew ? Promise.resolve(null) : api.get(`/api/properties/${property.id}`)),
    [property.id],
  );

  useEffect(() => {
    const source = detail.data;
    if (!source) return;
    setForm({
      name: source.name ?? '',
      customerId: source.customer_id ? String(source.customer_id) : '',
      referenceNo: source.reference_no ?? '',
      street: source.street ?? '',
      zip: source.zip ?? '',
      city: source.city ?? '',
      propertyType: source.property_type ?? '',
      unitsCount: source.units_count != null ? String(source.units_count) : '',
      contactName: source.contact_name ?? '',
      contactPhone: source.contact_phone ?? '',
      contactEmail: source.contact_email ?? '',
      latitude: source.latitude != null ? String(source.latitude) : '',
      longitude: source.longitude != null ? String(source.longitude) : '',
      accessNotes: source.access_notes ?? '',
      geofenceRadius: source.geofence_radius != null ? String(source.geofence_radius) : '',
    });
  }, [detail.data]);

  const set = (key: keyof typeof form, value: string) => setForm((current) => ({ ...current, [key]: value }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = {
        ...form,
        customerId: form.customerId ? Number(form.customerId) : null,
        unitsCount: form.unitsCount ? Number(form.unitsCount) : null,
        latitude: form.latitude ? Number(form.latitude) : null,
        longitude: form.longitude ? Number(form.longitude) : null,
        geofenceRadius: form.geofenceRadius ? Number(form.geofenceRadius) : null,
      };
      if (isNew) await api.post('/api/properties', payload);
      else await api.patch(`/api/properties/${property.id}`, payload);
      onSaved();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  /** Koordinaten aus der Anschrift – Grundlage für Karte und Tourenplanung. */
  const locate = async () => {
    const query = [form.street, form.zip, form.city].filter(Boolean).join(' ');
    if (query.length < 5) return;
    try {
      const hits = await api.get('/api/routing/geocode', { q: query });
      if (hits.length > 0) setForm((current) => ({ ...current, latitude: String(hits[0].lat), longitude: String(hits[0].lng) }));
    } catch {
      /* Der Adressdienst ist optional – Koordinaten lassen sich auch eintippen. */
    }
  };

  return (
    <Modal
      title={isNew ? 'Objekt anlegen' : `Objekt bearbeiten: ${property.name}`}
      onClose={onClose}
      size="wide"
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn primary" onClick={save} disabled={busy || form.name.trim().length < 2}>
            Speichern
          </button>
        </>
      }
    >
      {detail.loading ? (
        <Loading />
      ) : (
        <div className="stack">
          <ErrorMessage error={error} />
          <div className="form-grid">
            <Field label="Bezeichnung" required>
              <input value={form.name} onChange={(event) => set('name', event.target.value)} autoFocus />
            </Field>
            <Field label="Eigentümer/Verwalter">
              <SearchSelect
                value={String(form.customerId ?? '')}
                onChange={(wert) => set('customerId', wert)}
                options={(customers ?? []).map((customer: any) => ({ value: String(customer.id), label: String(customer.name) }))}
                placeholder="Ohne Zuordnung"
              />
            </Field>
            <Field label="Referenznummer">
              <input value={form.referenceNo} onChange={(event) => set('referenceNo', event.target.value)} />
            </Field>
            <Field label="Objektart">
              <input
                value={form.propertyType}
                onChange={(event) => set('propertyType', event.target.value)}
                placeholder="Wohnanlage, Bürogebäude …"
              />
            </Field>
            <Field label="Straße">
              <input value={form.street} onChange={(event) => set('street', event.target.value)} />
            </Field>
            <Field label="PLZ">
              <input value={form.zip} onChange={(event) => set('zip', event.target.value)} />
            </Field>
            <Field label="Ort">
              <input value={form.city} onChange={(event) => set('city', event.target.value)} />
            </Field>
            <Field label="Einheiten">
              <input type="number" value={form.unitsCount} onChange={(event) => set('unitsCount', event.target.value)} />
            </Field>
            <Field label="Ansprechpartner">
              <input value={form.contactName} onChange={(event) => set('contactName', event.target.value)} />
            </Field>
            <Field label="Telefon">
              <input value={form.contactPhone} onChange={(event) => set('contactPhone', event.target.value)} />
            </Field>
            <Field label="E-Mail">
              <input type="email" value={form.contactEmail} onChange={(event) => set('contactEmail', event.target.value)} />
            </Field>
          </div>

          <div className="form-grid">
            <Field label="Breitengrad" hint="Wird für Karte und Tourenplanung benötigt.">
              <input
                type="number"
                step="0.00001"
                value={form.latitude}
                onChange={(event) => set('latitude', event.target.value)}
              />
            </Field>
            <Field label="Längengrad">
              <input
                type="number"
                step="0.00001"
                value={form.longitude}
                onChange={(event) => set('longitude', event.target.value)}
              />
            </Field>
            <Field label="&nbsp;">
              <button className="btn" onClick={locate}>
                Koordinaten aus Anschrift
              </button>
            </Field>
          </div>

          <Field label="Zugang / Schlüssel" hint="Sensible Information – nur mit dem Recht properties.access sichtbar.">
            <textarea value={form.accessNotes} onChange={(event) => set('accessNotes', event.target.value)} />
          </Field>

          <span className="section-title">Anwesenheitsnachweis</span>
          <Field
            label="Geofence-Radius (m)"
            hint="Umkreis um die Anschrift, in dem sich am Auftrag ein- und ausstempeln lässt. Leer lassen für die Vorgabe des Betriebs."
          >
            <input
              type="number"
              min="20"
              max="2000"
              step="10"
              value={form.geofenceRadius}
              onChange={(event) => set('geofenceRadius', event.target.value)}
              placeholder="Vorgabe des Betriebs"
            />
          </Field>

          {!isNew && <PropertyTags propertyId={property.id} />}
        </div>
      )}
    </Modal>
  );
}

/**
 * NFC-Marken am Objekt.
 *
 * Der Aufkleber am Gebäude ist der zweite Weg, die Anwesenheit zu belegen –
 * er trägt dort, wo die Ortung versagt: im Keller, im Innenhof, zwischen
 * Hochhäusern. Angelegt wird er hier, gelesen auf dem Gerät des Außendienstes.
 *
 * Die Kennung wird auf dem Server vereinheitlicht (Großbuchstaben, ohne
 * Trennzeichen), weil Lesegeräte dieselbe Seriennummer mal mit Doppelpunkten
 * und mal ohne liefern.
 */
function PropertyTags({ propertyId }: { propertyId: number }) {
  const { notify } = useToast();
  const tags = useAsync(() => api.get(`/api/properties/${propertyId}/tags`), [propertyId]);
  const [form, setForm] = useState({ tagUid: '', label: '', locationHint: '' });
  const [busy, setBusy] = useState(false);

  const add = async () => {
    setBusy(true);
    try {
      await api.post(`/api/properties/${propertyId}/tags`, {
        tagUid: form.tagUid,
        label: form.label || null,
        locationHint: form.locationHint || null,
      });
      setForm({ tagUid: '', label: '', locationHint: '' });
      tags.reload();
      notify('NFC-Marke hinterlegt.', 'success');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Die Marke konnte nicht angelegt werden.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (tag: any) => {
    if (!window.confirm(`Marke ${tag.tag_uid} stilllegen? Bereits erfasste Buchungen bleiben nachvollziehbar.`)) return;
    await api.delete(`/api/properties/${propertyId}/tags/${tag.id}`);
    tags.reload();
    notify('Marke stillgelegt.', 'success');
  };

  const active = (tags.data ?? []).filter((tag: any) => tag.is_active);

  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="row between">
        <span className="small subtle">
          NFC-Marken am Objekt {active.length > 0 ? `(${active.length} aktiv)` : '– noch keine hinterlegt'}
        </span>
      </div>

      {(tags.data ?? []).length > 0 && (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Kennung</th>
                <th>Bezeichnung</th>
                <th>Anbringung</th>
                <th>Zuletzt gelesen</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(tags.data ?? []).map((tag: any) => (
                <tr key={tag.id} style={tag.is_active ? undefined : { opacity: 0.5 }}>
                  <td className="mono small">{tag.tag_uid}</td>
                  <td className="small">{tag.label ?? '–'}</td>
                  <td className="small subtle">{tag.location_hint ?? '–'}</td>
                  <td className="small subtle">{tag.last_seen_at ? formatDateTime(tag.last_seen_at) : 'nie'}</td>
                  <td className="actions">
                    {tag.is_active ? (
                      <button className="btn sm ghost" onClick={() => remove(tag)} title="Stilllegen">
                        Stilllegen
                      </button>
                    ) : (
                      <Badge>Stillgelegt</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="form-grid">
        <Field label="Kennung der Marke" hint="Seriennummer des Transponders oder aufgedruckter Code.">
          <input
            value={form.tagUid}
            onChange={(event) => setForm((current) => ({ ...current, tagUid: event.target.value }))}
            placeholder="04A3B7C21D5E80"
          />
        </Field>
        <Field label="Bezeichnung">
          <input
            value={form.label}
            onChange={(event) => setForm((current) => ({ ...current, label: event.target.value }))}
            placeholder="Haupteingang"
          />
        </Field>
        <Field label="Anbringung">
          <input
            value={form.locationHint}
            onChange={(event) => setForm((current) => ({ ...current, locationHint: event.target.value }))}
            placeholder="Innen links neben der Klingelanlage"
          />
        </Field>
      </div>
      <div className="row">
        <button className="btn sm" onClick={add} disabled={busy || form.tagUid.trim().length < 4}>
          Marke hinterlegen
        </button>
      </div>
    </div>
  );
}
